import React, { useState, useEffect } from "react";
import { IEvent, IShow, IScore } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import { apiService } from "../../../services/api.service";
import { toast } from "../../Toast/ToastManager";
import AwardDetailsModal, { IAwardDetails } from "../AwardDetailsModal/AwardDetailsModal";

import "./AkcRallyObedienceAwards.scss";

interface IProps {
  event: IEvent;
  show: IShow;
  scores?: IScore[];
}

interface IAward {
  registered_name: string;
  breed: string;
  total: number;
  totalTime: number;
  arm_number: string;
}

interface IAwardsData {
  highCombined?: IAward;
  highCombinedDetails?: IAwardDetails[];
  highTriple?: IAward;
  highTripleDetails?: IAwardDetails[];
  specialtyHighCombined?: IAward;
  specialtyHighCombinedDetails?: IAwardDetails[];
  specialtyHighTriple?: IAward;
  specialtyHighTripleDetails?: IAwardDetails[];
}

const AkcRallyObedienceAwards: React.FC<IProps> = ({ event, show, scores: providedScores }) => {
  const [loading, setLoading] = useState(!providedScores);
  const [awards, setAwards] = useState<IAwardsData | null>(null);
  const [detailsModal, setDetailsModal] = useState<{
    isOpen: boolean;
    awardType: 'highCombined' | 'highTriple' | null;
    details: IAwardDetails[];
    title: string;
  }>({
    isOpen: false,
    awardType: null,
    details: [],
    title: '',
  });

  useEffect(() => {
    loadScoresAndCalculateAwards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event._id, show._id, providedScores]);

  const loadScoresAndCalculateAwards = async () => {
    try {
      setLoading(true);
      const scores = providedScores || await apiService.getEventScores(event._id.toString());

      // Filter scores for this show
      const showScores = scores.filter(
        (s) =>
          s.show_type === Constants.show_type.rally_obedience &&
          s.show_id.toString() === show._id.toString() &&
          s.rally_obedience?.result_type === Constants.score_result_type.qualified
      );

      const calculatedAwards = calculateAwards(showScores);
      setAwards(calculatedAwards);
    } catch (error) {
      toast.show({
        title: "Error",
        content: "Failed to load scores for awards calculation",
        duration: 5000,
        type: "fail",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateAwards = (scores: IScore[]): IAwardsData => {
    const rallyCombinedLevels = [
      Constants.dog_class_element_level.advanced_b,
      Constants.dog_class_element_level.excellent_b,
    ];

    const rallyTripleLevels = [
      Constants.dog_class_element_level.advanced_b,
      Constants.dog_class_element_level.excellent_b,
      Constants.dog_class_element_level.master,
    ];

    // Calculate regular awards
    const regularAwards = calculateAwardsForClasses(scores, rallyCombinedLevels, rallyTripleLevels);

    // Calculate specialty awards if applicable
    let specialtyAwards;
    if (show.specialty_breed && show.specialty_breed.length > 0) {
      const specialtyScores = scores.filter((s) => s.breed === show.specialty_breed);
      specialtyAwards = calculateAwardsForClasses(specialtyScores, rallyCombinedLevels, rallyTripleLevels);
    }

    return {
      highCombined: regularAwards.highCombined,
      highCombinedDetails: regularAwards.highCombinedDetails,
      highTriple: regularAwards.highTriple,
      highTripleDetails: regularAwards.highTripleDetails,
      specialtyHighCombined: specialtyAwards?.highCombined,
      specialtyHighCombinedDetails: specialtyAwards?.highCombinedDetails,
      specialtyHighTriple: specialtyAwards?.highTriple,
      specialtyHighTripleDetails: specialtyAwards?.highTripleDetails,
    };
  };

  const calculateAwardsForClasses = (
    scores: IScore[],
    rallyCombinedLevels: string[],
    rallyTripleLevels: string[]
  ) => {
    // Group scores by dog and level
    const dogScores = new Map<string, Record<string, any>>();

    scores.forEach((s) => {
      const dogId = s.dog_id.toString();
      const level = s.dog_class?.level || "";
      const deductions = s.rally_obedience?.total_deductions_only
        ? s.rally_obedience?.total_deductions ?? 0
        : (s.rally_obedience?.element_deductions || []).reduce(
            (prev, current) =>
              prev +
              (isNaN(parseFloat(current.element_deductions))
                ? 0
                : parseFloat(current.element_deductions)),
            0
          );
      const score = 100 - deductions;
      const time = s.rally_obedience?.time || 0;

      if (!dogScores.has(dogId)) {
        dogScores.set(dogId, {
          registered_name: s.registered_name || "",
          breed: s.breed || "",
          arm_number: s.arm_number || "",
        });
      }

      dogScores.get(dogId)![level] = score;
      dogScores.get(dogId)![`${level}_time`] = time;
    });

    // Calculate High Combined (Advanced B + Excellent B) - collect all candidates
    const highCombinedCandidates: IAwardDetails[] = [];
    dogScores.forEach((levels, dogId) => {
      if (rallyCombinedLevels.every((lvl) => typeof levels[lvl] === "number")) {
        const total = rallyCombinedLevels.reduce((sum, lvl) => sum + (levels[lvl] as number), 0);
        const totalTime = rallyCombinedLevels.reduce((sum, lvl) => sum + (levels[`${lvl}_time`] as number || 0), 0);

        highCombinedCandidates.push({
          registered_name: levels.registered_name,
          breed: levels.breed,
          arm_number: levels.arm_number,
          advancedBScore: levels[Constants.dog_class_element_level.advanced_b],
          excellentBScore: levels[Constants.dog_class_element_level.excellent_b],
          total,
          totalTime,
          advancedBTime: levels[`${Constants.dog_class_element_level.advanced_b}_time`],
          excellentBTime: levels[`${Constants.dog_class_element_level.excellent_b}_time`],
        });
      }
    });

    // Sort by total (desc), then by time (asc)
    highCombinedCandidates.sort((a, b) => {
      if (a.total !== b.total) return b.total - a.total;
      return a.totalTime - b.totalTime;
    });

    // Calculate High Triple (Advanced B + Excellent B + Master) - collect all candidates
    const highTripleCandidates: IAwardDetails[] = [];
    dogScores.forEach((levels, dogId) => {
      if (rallyTripleLevels.every((lvl) => typeof levels[lvl] === "number")) {
        const total = rallyTripleLevels.reduce((sum, lvl) => sum + (levels[lvl] as number), 0);
        const totalTime = rallyTripleLevels.reduce((sum, lvl) => sum + (levels[`${lvl}_time`] as number || 0), 0);

        highTripleCandidates.push({
          registered_name: levels.registered_name,
          breed: levels.breed,
          arm_number: levels.arm_number,
          advancedBScore: levels[Constants.dog_class_element_level.advanced_b],
          excellentBScore: levels[Constants.dog_class_element_level.excellent_b],
          masterScore: levels[Constants.dog_class_element_level.master],
          total,
          totalTime,
          advancedBTime: levels[`${Constants.dog_class_element_level.advanced_b}_time`],
          excellentBTime: levels[`${Constants.dog_class_element_level.excellent_b}_time`],
          masterTime: levels[`${Constants.dog_class_element_level.master}_time`],
        });
      }
    });

    // Sort by total (desc), then by time (asc)
    highTripleCandidates.sort((a, b) => {
      if (a.total !== b.total) return b.total - a.total;
      return a.totalTime - b.totalTime;
    });

    return {
      highCombined: highCombinedCandidates.length > 0 ? {
        registered_name: highCombinedCandidates[0].registered_name,
        breed: highCombinedCandidates[0].breed,
        total: highCombinedCandidates[0].total,
        totalTime: highCombinedCandidates[0].totalTime,
        arm_number: highCombinedCandidates[0].arm_number,
      } : undefined,
      highCombinedDetails: highCombinedCandidates,
      highTriple: highTripleCandidates.length > 0 ? {
        registered_name: highTripleCandidates[0].registered_name,
        breed: highTripleCandidates[0].breed,
        total: highTripleCandidates[0].total,
        totalTime: highTripleCandidates[0].totalTime,
        arm_number: highTripleCandidates[0].arm_number,
      } : undefined,
      highTripleDetails: highTripleCandidates,
    };
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.round((seconds % 1) * 100);
    return `${minutes}:${secs.toString().padStart(2, "0")}.${ms.toString().padStart(2, "0")}`;
  };

  const openDetailsModal = (title: string, details: IAwardDetails[], awardType: 'highCombined' | 'highTriple') => {
    setDetailsModal({
      isOpen: true,
      awardType,
      details,
      title,
    });
  };

  const closeDetailsModal = () => {
    setDetailsModal({
      isOpen: false,
      awardType: null,
      details: [],
      title: '',
    });
  };

  const renderAwardSection = (title: string, award: IAward | undefined, details?: IAwardDetails[], awardType?: 'highCombined' | 'highTriple') => {
    return (
      <div className="awardSection">
        <h4>{title}</h4>
        {award ? (
          <>
            <p className="awardWinner">
              #{award.arm_number} {award.registered_name} ({award.breed}) – Score {award.total}, Time {formatTime(award.totalTime)}
            </p>
            {details && details.length > 0 && awardType && (
              <button className="viewDetailsLink" onClick={() => openDetailsModal(title, details, awardType)}>
                View Details
              </button>
            )}
          </>
        ) : (
          <p className="noWinner">No qualifying scores for this award</p>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="AkcRallyObedienceAwards loading">Loading awards...</div>;
  }

  if (!awards) {
    return <div className="AkcRallyObedienceAwards error">Failed to calculate awards</div>;
  }

  return (
    <>
      <div className="AkcRallyObedienceAwards">
        <div className="regularAwards">
          {renderAwardSection("High Combined (Advanced B + Excellent B)", awards.highCombined, awards.highCombinedDetails, 'highCombined')}
          {renderAwardSection("High Triple (Advanced B + Excellent B + Master)", awards.highTriple, awards.highTripleDetails, 'highTriple')}
        </div>

        {show.specialty_breed && show.specialty_breed.length > 0 && (awards.specialtyHighCombined || awards.specialtyHighTriple) && (
          <div className="specialtyAwards">
            <div className="specialtyDivider"></div>
            {renderAwardSection(`(${show.specialty_breed}) High Combined (Advanced B + Excellent B)`, awards.specialtyHighCombined, awards.specialtyHighCombinedDetails, 'highCombined')}
            {renderAwardSection(`(${show.specialty_breed}) High Triple (Advanced B + Excellent B + Master)`, awards.specialtyHighTriple, awards.specialtyHighTripleDetails, 'highTriple')}
          </div>
        )}
      </div>
      
      <AwardDetailsModal
        isOpen={detailsModal.isOpen}
        onClose={closeDetailsModal}
        awardTitle={detailsModal.title}
        showName={show.custom_show_name || show.show_name || ""}
        showType={(Constants.show_type as any)[show.show_type] || show.show_type}
        showDate={event.start_date ? new Date(event.start_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : ""}
        details={detailsModal.details}
        awardType={detailsModal.awardType || 'highCombined'}
      />
    </>
  );
};

export default AkcRallyObedienceAwards;

